<template>
    <xx-treeselect
        class="xx-categoryselect"
        ref="treeselect"
        v-model="innerValue"
        :disabled="disabled"
        :multiple="multiple"
        :data="showData"
        :name="name"
        :node-config="nodeConfig"
        :renderTag="renderTag()"
        :placeholder="placeholder"
        :onlyLeaf="leafOnly"
        :editable-method="isSelectLevel"
        v-bind="$attrs"
        v-on="listeners"
        @change="handleChange"
    ></xx-treeselect>
</template>

<script>
import Vue from 'vue';
import { deepClone, debounce } from '@lsfe/tools';
import remoteMixin from '../../mixins/remote';

const fetchDebounceBuffer = {};

const dataBuffer = {};

const getData = (url, body, vmList) => {
    return Vue.prototype.$hPost(url, body, {cached: true}).then(data => {
        vmList.forEach(vm => {
            // 原有逻辑不变，增加后台类目的缓存逻辑
            if (vm.useBgCategory) {
                dataBuffer[`${vm.permission}-categoryType-${vm.categoryType}`] = data;
            } else {
                dataBuffer[`${vm.permission}-${vm.poiId}`] = data;
            }
            vm.handleData(data);
        });
    });
};

function parseCategoryName(categoryList, type) {
    for (let index = 0; index < categoryList.length; index++) {
        categoryList[index].parsedName = `${categoryList[index].id}-${categoryList[index].name}`;
        if (type === 'number') {
            categoryList[index].id = +categoryList[index].id;
            categoryList[index].parentId = +categoryList[index].parentId;
        }
    }
    return categoryList;
}

export default {
    name: 'XxCategoryselect',
    props: {
        name: String,
        value: [Number, String, Array, Object],
        disabled: {
            type: Boolean,
            default: false
        },
        disabledIds: Array,
        maxLevel: Number,
        selectLevel: [Number, String, Array], // 1,2,3,4,5
        poiId: {
            type: Number,
            default: -1
        },
        placeholder: {
            type: String,
            default: '请选择商品分类'
        },
        // 接收数据数组中，想要放到label上的属性的属性名，默认是name
        label: {
            type: [String, Function],
            default: 'name'
        },
        // 是否是多选模式
        multiple: {
            type: Boolean,
            default: true
        },
        // 是否权限限制
        permission: {
            type: Boolean,
            default: true
        },
        bgPermission: {
            type: Boolean,
            default: false,
        },
        // 是否只允许选择叶子节点
        leafOnly: {
            type: Boolean,
            default: false
        },
        // 标签处理函数
        tagRender: {
            type: Function
        },
        tagFilter: {
            type: Function
        },
        // 多于条目的展示
        showEllipsis: {
            type: [Function, Boolean]
        },
        // /**
        //  * 处理数据源返回的数据
        //  */
        // responseProcessor: {
        //     type: Function
        // },
        useAllTagData: {
            type: Boolean,
            default: true
        },
        valueType: {
            type: String,
            default: 'string'
        },
        // 是否使用后台类目，默认false
        useBgCategory: {
            type: Boolean,
            default: false,
        },
        // 分类类型：1 经营品，2 物资
        categoryType: {
            type: Number,
            default: 0,
        },
        // 接口请求缓存，默认开启
        useRequestCache: {
            type: Boolean,
            default: true,
        }
    },
    mixins: [remoteMixin()],
    data() {
        return {
            innerValue: this.value,
            categoryData: [],
            nodeConfig: {
                label: 'name'
            },
            bufferKey: '',
            renderLabel: null // 该方法用于解决label重传问题
        };
    },
    watch: {
        value: {
            immediate: true,
            handler() {
                this.innerValue = this.value;
            }
        },
        categoryType() {
            this.getAllCategoryList();
        }
    },
    computed: {
        showData() {
            return this.categoryData
                .filter(item => !this.maxLevel || item.level <= this.maxLevel)
                .map(item => {
                    // 设置禁用
                    item.disabled = this._isItemDisabled(item);
                    // 设置label
                    if (this.renderLabel) {
                        item.label = this.renderLabel(item);
                    }
                    return item;
                });
        },
        listeners() {
            const { change, ...listeners } = this.$listeners;
            return listeners;
        },
        innerRenderTag() {
            if (this.tagRender || this.tagFilter) {
                return data => {
                    const dataAfterHandle = [];
                    data.forEach((item, index) => {
                        if (this.tagFilter && !this.tagFilter(item, index)) {
                            return;
                        }
                        if (this.tagRender && typeof this.tagRender === 'function') {
                            item.name = this.tagRender(item);
                        }
                        dataAfterHandle.push(item);
                    });
                    return dataAfterHandle;
                };
            }
        }
    },
    created() {
        if (this.label && typeof this.label === 'string') {
            this.nodeConfig.label = this.label;
        }
        if (typeof this.label === 'function') {
            this.nodeConfig.label = 'label';
            this.renderLabel = this.label;
        }
        if (!this.remoteConfig) {
            this.getAllCategoryList();
        }
    },
    beforeDestroy() {
        // 可能存在dataBuffer取数据的后创建的组件，所以fetchDebounceBuffer[this.bufferKey]不一定存在
        const {vmList} = fetchDebounceBuffer[this.bufferKey] || {};
        const index = (vmList || []).findIndex(vm => vm === this);
        if (index === -1) {
            return;
        }
        vmList.splice(index, 1);
    },
    methods: {
        $reset() {
            const type = typeof this.value;
            const resetValueEnum = {
                string: '',
                number: -1,
                object: []
            };
            this.$emit('input', resetValueEnum[type]);
        },
        _isItemDisabled(item) {
            // 如果设置了disabledIds，并且当前Item命中了disabledId
            let isDisabled = false;
            if (this.disabledIds && (this.disabledIds.includes(item.id) || this.disabledIds.includes(+item.id))) {
                isDisabled = true;
            }
            return isDisabled;
        },
        $_remoteDataLoaded(data) {
            this.categoryData = deepClone(data);
        },
        handleData(data) {
            // 适配后台类目接口字段
            (data || []).forEach(item => {
                item.id = item.id || item.categoryId;
            });
            let dataTmp = data && data.length ? parseCategoryName(data, this.valueType) : [];
            if (this.responseProcessor && typeof this.responseProcessor === 'function') {
                dataTmp = this.responseProcessor(dataTmp);
            }
            this.categoryData = deepClone(dataTmp);
            this.$emit('loaded', this.categoryData);
        },
        getAllCategoryList() {
            let url = this.permission ? '/api/resource/getCategoryList' : '/api/resource/getCategoryListAll';
            let body = {
                poiId: this.poiId,
            };
            this.bufferKey = `${this.permission}-${this.poiId}`;
            if (this.useBgCategory) {
                url = this.bgPermission ? '/shepherd/product/bgcategory/queryWithPermission' : '/shepherd/product/bgcategory/query';
                body = {
                    categoryType: this.categoryType, // 品类类型 1 商品 2 物资，0 默认返回全集
                };
                // 后台类目用【分类类型】作为缓存key
                // todo 如果后续后台类目也需要支持门店纬度筛选，则需要调整缓存key
                this.bufferKey = `${this.bgPermission}-categoryType-${this.categoryType}`;
            }
            // 请求数据添加到缓存，优先级最高
            if (dataBuffer[this.bufferKey]) {
                return this.handleData(dataBuffer[this.bufferKey]);
            }
            // 增加缓存开关，默认开启，多次引用只发起一次请求，适用于一个组件被多次调用，比如列表场景
            // 当关闭缓存，多次调用会发起多次请求，适用于同时使用管理类目和后台类目
            if (this.useRequestCache) {
                if (!fetchDebounceBuffer[this.bufferKey]) {
                    fetchDebounceBuffer[this.bufferKey] = {
                        method: debounce(getData, 300),
                        vmList: [this]
                    };
                } else {
                    fetchDebounceBuffer[this.bufferKey].vmList.push(this);
                }
            } else {
                fetchDebounceBuffer[this.bufferKey] = {
                    method: debounce(getData, 300),
                    vmList: [this]
                };
            }
            fetchDebounceBuffer[this.bufferKey].method(url, body, fetchDebounceBuffer[this.bufferKey].vmList);
        },
        isSelectLevel(node) {
            let level = Number(node.level);
            if (!this.selectLevel) {
                // 没有selectLevel 直接通过
                return true;
            }
            if (Array.isArray(this.selectLevel)) {
                // selectLevel是数组
                return ~this.selectLevel.indexOf(level);
            }
            if (typeof this.selectLevel === 'number' || typeof this.selectLevel === 'string') {
                // selectLevel是数字 或 字符串
                return Number(this.selectLevel) === level;
            }
        },
        handleChange(val, item) {
            this.$emit('change', val, item);
            // // 下面事件是为兼容旧版事件，不推荐使用
            // const nodesValue = val ? [].concat(item) : [];
            // this.$emit('selected-nodes-change', nodesValue);
        },
        renderTag() {
            if (!this.innerRenderTag || typeof this.innerRenderTag !== 'function') {
                return;
            }
            return (h, data, treeselect, tagWrapper) => {
                if (!this.useAllTagData) {
                    data = tagWrapper.formatTagData(data);
                }
                const tagData = this.innerRenderTag(deepClone(data));
                let extendTag = '';
                if (tagData.length < data.length && this.showEllipsis) {
                    extendTag = '...';
                    if (typeof this.showEllipsis === 'function') {
                        extendTag = this.showEllipsis(data);
                    }
                }
                const extend = extendTag
                    ? [
                        <el-tag size="small" key="extendTag" disable-transitions={true}>
                            {extendTag}
                        </el-tag>
                    ]
                    : [];
                return tagData
                    .map(item => (
                        <el-tag
                            size="small"
                            key={item[this.nodeConfig.value || 'id']}
                            disable-transitions={true}
                            closable={!treeselect.selectDisabled}
                            onClose={tagWrapper.deleteOneData(item)}
                        >
                            {item[this.nodeConfig.label]}
                        </el-tag>
                    ))
                    .concat(extend);
            };
        }
    }
};
</script>
<style lang="scss" scoped>
.xx-categoryselect {
    width: 180px;
}
</style>
